import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantStore } from '@/stores/tenant'
import { requiresProctorMfa } from '@/lib/proctor-roles'
import { isIdleExpired, readLastActivity } from '@/lib/idle-session'

// Re-exported so existing importers keep working; the definition lives in a
// dependency-free module so views can share it without constructing a router.
export { requiresProctorMfa }

/** Teacher/admin app sessions must re-auth via /proctor/login before proctor tools. */
export function requiresProctorReauthentication(
  isAuthenticated: boolean,
  role: string | null | undefined,
  hasProctorMfa: boolean,
): boolean {
  if (!isAuthenticated) return false
  if (role !== 'teacher' && role !== 'admin') return false
  return !hasProctorMfa
}

/** Students keep their exam session; teacher/admin/proctor without MFA re-auth. */
export function mustSignOutForProctorLogin(
  isAuthenticated: boolean,
  role: string | null | undefined,
  hasProctorMfa: boolean,
): boolean {
  if (!isAuthenticated || hasProctorMfa) return false
  return requiresProctorMfa(role)
    || requiresProctorReauthentication(isAuthenticated, role, hasProctorMfa)
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { hideForAuthenticated: true, allowUnknownTenant: true },
  },
  {
    path: '/proctor/login',
    name: 'proctor-login',
    component: () => import('@/views/ProctorLogin.vue'),
  },
  {
    path: '/proctor/enroll-totp',
    name: 'proctor-totp-enrollment',
    component: () => import('@/views/ProctorTotpEnrollment.vue'),
    meta: { requiresAuth: true, allowsProctorEnrollment: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    // Landing target for the AIMSIS academic → CBT single sign-on redirect.
    // Exchanges the `?token=` handoff JWT for a Firebase session, then routes on.
    path: '/auth/handoff',
    name: 'auth-handoff',
    component: () => import('@/views/AuthHandoff.vue'),
  },
  {
    // Forced landing spot after an admin password reset — see
    // POST /admin/users/:uid/reset-password and the mustChangePassword claim.
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/tenant-not-found',
    name: 'tenant-not-found',
    component: () => import('@/views/TenantNotFound.vue'),
    meta: { allowUnknownTenant: true },
  },

  // ---- student exam flow ------------------------------------------------
  {
    path: '/quiz/:quizId/start',
    name: 'quiz-passcode',
    component: () => import('@/views/PasscodeEntry.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/quiz/:quizId/instructions',
    name: 'quiz-instructions',
    component: () => import('@/views/Instructions.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/quiz/:quizId/exam',
    name: 'quiz-exam',
    component: () => import('@/views/ExamRunner.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/quiz/:quizId/submitted/:sessionId',
    name: 'quiz-submitted',
    component: () => import('@/views/ExamSubmitted.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/results',
    name: 'student-results',
    component: () => import('@/views/StudentHistory.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/exams',
    name: 'student-exams',
    component: () => import('@/views/StudentExams.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },
  {
    path: '/settings',
    name: 'student-settings',
    component: () => import('@/views/StudentSettings.vue'),
    meta: { requiresAuth: true, role: 'student' },
  },

  // ---- proctor ---------------------------------------------------------
  {
    path: '/proctor',
    name: 'proctor-exam-picker',
    component: () => import('@/views/ProctorExamPicker.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    path: '/proctor/exams/:quizId',
    name: 'proctor-monitor',
    component: () => import('@/views/ProctorDashboard.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    path: '/proctor/settings',
    name: 'proctor-settings',
    component: () => import('@/views/ProctorSettings.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    // Global inbox: every quiz this proctor is assigned to, grouped by quiz.
    // `?quiz=<id>` preselects one, which is what the exam monitor links with.
    path: '/proctor/unlock-inbox',
    name: 'proctor-unlock-inbox',
    component: () => import('@/views/ProctorUnlockInbox.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    // The inbox used to live under the exam it belonged to. Keep the old URL
    // working for bookmarks and any link that still carries a quiz id.
    path: '/proctor/exams/:quizId/unlock-inbox',
    redirect: (to) => ({
      name: 'proctor-unlock-inbox',
      query: { quiz: String(to.params.quizId) },
    }),
  },
  {
    path: '/proctor/exams/:quizId/unlock-inbox/:requestId',
    name: 'proctor-unlock-decision',
    component: () => import('@/views/ProctorUnlockDecision.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    path: '/proctor/exams/:quizId/sessions/:sessionId',
    name: 'proctor-session',
    component: () => import('@/views/ProctorDashboard.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },
  {
    // Same Access codes page teachers and admins use; the page derives its
    // back link from the route path.
    path: '/proctor/access-codes',
    name: 'proctor-access-codes',
    component: () => import('@/views/teacher/AccessCodes.vue'),
    meta: { requiresAuth: true, requiresProctorMfa: true, anyRole: ['proctor', 'teacher', 'admin'] },
  },

  // ---- admin -----------------------------------------------------------
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('@/views/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/users/new',
    name: 'admin-user-new',
    component: () => import('@/views/admin/UserEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/users/:uid',
    name: 'admin-user-editor',
    component: () => import('@/views/admin/UserEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/quizzes',
    name: 'admin-quizzes',
    component: () => import('@/views/admin/QuizzesList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/quizzes/new',
    redirect: { name: 'admin-quiz-editor' },
  },
  {
    path: '/admin/quizzes/edit/:quizId?',
    name: 'admin-quiz-editor',
    component: () => import('@/views/admin/QuizEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    // Bookmark/back-compat for the old `/admin/quizzes/:quizId` shape.
    path: '/admin/quizzes/:quizId',
    redirect: (to) => ({ name: 'admin-quiz-editor', params: { quizId: to.params.quizId } }),
  },
  {
    path: '/admin/classrooms',
    name: 'admin-classrooms',
    component: () => import('@/views/admin/ClassroomList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/classrooms/new',
    name: 'admin-classroom-new',
    component: () => import('@/views/admin/ClassroomEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/classrooms/:id',
    name: 'admin-classroom-editor',
    component: () => import('@/views/admin/ClassroomEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic',
    name: 'admin-academic',
    component: () => import('@/views/admin/AcademicSetup.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/grades',
    name: 'admin-grade-levels',
    component: () => import('@/views/admin/GradeLevelList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/grades/new',
    name: 'admin-grade-level-new',
    component: () => import('@/views/admin/GradeLevelEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/grades/:id',
    name: 'admin-grade-level-editor',
    component: () => import('@/views/admin/GradeLevelEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/subjects',
    name: 'admin-subjects',
    component: () => import('@/views/admin/SubjectList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/subjects/new',
    name: 'admin-subject-new',
    component: () => import('@/views/admin/SubjectEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/subjects/:id',
    name: 'admin-subject-editor',
    component: () => import('@/views/admin/SubjectEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/class-memberships',
    name: 'admin-class-memberships',
    component: () => import('@/views/admin/ClassMembershipList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/class-memberships/new',
    name: 'admin-class-membership-new',
    component: () => import('@/views/admin/ClassMembershipEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/class-memberships/:id',
    name: 'admin-class-membership-editor',
    component: () => import('@/views/admin/ClassMembershipEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/teaching-assignments',
    name: 'admin-teaching-assignments',
    component: () => import('@/views/admin/TeachingAssignmentList.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/teaching-assignments/new',
    name: 'admin-teaching-assignment-new',
    component: () => import('@/views/admin/TeachingAssignmentEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/teaching-assignments/:id',
    name: 'admin-teaching-assignment-editor',
    component: () => import('@/views/admin/TeachingAssignmentEditor.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/academic/legacy-migration',
    name: 'admin-legacy-academic-migration',
    component: () => import('@/views/admin/LegacyAcademicMigration.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/sync',
    name: 'admin-sync',
    component: () => import('@/views/admin/SyncAdmin.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/reports',
    name: 'admin-reports',
    component: () => import('@/views/teacher/Reports.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/reports/quizzes/:quizId',
    name: 'admin-report-quiz',
    component: () => import('@/views/reports/QuizReport.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/reports/participants',
    name: 'admin-report-participants',
    component: () => import('@/views/reports/ParticipantView.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/reports/participants/:studentId/history',
    name: 'admin-report-participant-history',
    component: () => import('@/views/reports/ParticipantHistory.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/reports/sessions/:sessionId',
    name: 'admin-report-session',
    component: () => import('@/views/reports/SessionReport.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },
  {
    path: '/admin/access-codes',
    name: 'admin-access-codes',
    component: () => import('@/views/teacher/AccessCodes.vue'),
    meta: { requiresAuth: true, role: 'admin' },
  },

  // ---- teacher ---------------------------------------------------------
  {
    path: '/teacher',
    name: 'teacher-dashboard',
    component: () => import('@/views/teacher/TeacherDashboard.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/quizzes',
    name: 'teacher-quizzes',
    component: () => import('@/views/admin/QuizzesList.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/quizzes/new',
    redirect: { name: 'teacher-quiz-editor' },
  },
  {
    path: '/teacher/quizzes/edit/:quizId?',
    name: 'teacher-quiz-editor',
    component: () => import('@/views/admin/QuizEditor.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    // Bookmark/back-compat for the old `/teacher/quizzes/:quizId` shape.
    path: '/teacher/quizzes/:quizId',
    redirect: (to) => ({ name: 'teacher-quiz-editor', params: { quizId: to.params.quizId } }),
  },
  {
    path: '/teacher/grading',
    name: 'teacher-grading',
    component: () => import('@/views/teacher/GradingQueue.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/reports',
    name: 'teacher-reports',
    component: () => import('@/views/teacher/Reports.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/reports/quizzes/:quizId',
    name: 'teacher-report-quiz',
    component: () => import('@/views/reports/QuizReport.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/reports/participants',
    name: 'teacher-report-participants',
    component: () => import('@/views/reports/ParticipantView.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/reports/participants/:studentId/history',
    name: 'teacher-report-participant-history',
    component: () => import('@/views/reports/ParticipantHistory.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/reports/sessions/:sessionId',
    name: 'teacher-report-session',
    component: () => import('@/views/reports/SessionReport.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/access-codes',
    name: 'teacher-access-codes',
    component: () => import('@/views/teacher/AccessCodes.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/question-bank',
    name: 'teacher-question-bank',
    component: () => import('@/views/teacher/QuestionBank.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/question-bank/ai',
    name: 'teacher-question-ai',
    component: () => import('@/views/teacher/AiQuestionGenerator.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/question-bank/new',
    name: 'teacher-question-create',
    component: () => import('@/views/teacher/QuestionBankEditor.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/question-bank/:itemId/edit',
    name: 'teacher-question-edit',
    component: () => import('@/views/teacher/QuestionBankEditor.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },
  {
    path: '/teacher/grading/:sessionId',
    name: 'teacher-grade-session',
    component: () => import('@/views/teacher/GradeSession.vue'),
    meta: { requiresAuth: true, anyRole: ['teacher', 'admin'] },
  },

  // ---- dev / internal --------------------------------------------------
  {
    path: '/health',
    name: 'health',
    component: () => import('@/views/HealthCheck.vue'),
  },
  {
    path: '/__components',
    name: 'components-preview',
    component: () => import('@/views/ComponentsPreview.vue'),
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const tenant = useTenantStore()
  tenant.refreshFromLocation()

  // Public diagnostics and the internal component catalog do not depend on
  // Firebase Auth. Let them render even when local auth configuration is absent.
  if (!auth.ready && (to.meta.requiresAuth || to.meta.hideForAuthenticated)) {
    await waitUntilReady(auth)
  }

  if (!to.meta.allowUnknownTenant && tenant.prodLikeHost && !tenant.slug) {
    return { name: 'tenant-not-found' }
  }

  // A session idle past the limit is dropped here too — a stale tab cannot
  // navigate its way back into protected pages between watcher ticks.
  if (to.meta.requiresAuth && auth.isAuthenticated && isIdleExpired(readLastActivity())) {
    const login = to.meta.requiresProctorMfa || to.meta.allowsProctorEnrollment
      ? 'proctor-login'
      : 'login'
    try {
      await auth.signOut()
    } catch {
      // fbSignOut can reject on storage-blocked browsers; the local state is
      // already cleared, so fall through to the login redirect.
    }
    return { name: login, query: { error: 'session_expired', next: to.fullPath } }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    if (to.meta.requiresProctorMfa || to.meta.allowsProctorEnrollment) {
      return { name: 'proctor-login', query: { next: to.fullPath } }
    }
    return { name: 'login', query: { next: to.fullPath } }
  }

  if (auth.isAuthenticated && tenant.slug && auth.schoolId && auth.schoolId !== tenant.slug) {
    await auth.signOut()
    return {
      name: 'login',
      query: { error: 'tenant_mismatch', next: to.fullPath },
    }
  }

  if (to.name === 'proctor-login' && auth.isAuthenticated) {
    if (auth.hasProctorMfa) return { name: 'proctor-exam-picker' }
    if (mustSignOutForProctorLogin(auth.isAuthenticated, auth.role, auth.hasProctorMfa)) {
      await auth.signOut()
    } else {
      return { name: 'dashboard' }
    }
  }

  if (
    auth.isAuthenticated
    && requiresProctorMfa(auth.role)
    && !auth.hasProctorMfa
    && !to.meta.allowsProctorEnrollment
  ) {
    await auth.signOut()
    return { name: 'proctor-login', query: { error: 'mfa_required' } }
  }

  const needsProctorLogin =
    requiresProctorMfa(auth.role)
    || requiresProctorReauthentication(auth.isAuthenticated, auth.role, auth.hasProctorMfa)
  if (to.meta.requiresProctorMfa && needsProctorLogin && !auth.hasProctorMfa) {
    await auth.signOut()
    return { name: 'proctor-login', query: { error: 'mfa_required', next: to.fullPath } }
  }

  // A pending admin password reset must be resolved before anything else.
  if (auth.isAuthenticated) {
    if (auth.mustChangePassword && to.name !== 'change-password' && !to.meta.allowsProctorEnrollment) {
      return { name: 'change-password' }
    }
    if (to.name === 'change-password' && !auth.mustChangePassword) {
      return { name: 'dashboard' }
    }
  }

  if (to.meta.hideForAuthenticated && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  // Exact-role check.
  if (to.meta.role && auth.role !== to.meta.role) {
    return { name: 'dashboard' }
  }
  // Any-of-roles check.
  if (Array.isArray(to.meta.anyRole) && !to.meta.anyRole.includes(auth.role)) {
    return { name: 'dashboard' }
  }
  return true
})

function waitUntilReady(auth: ReturnType<typeof useAuthStore>): Promise<void> {
  return new Promise((resolve) => {
    const stop = watch(
      () => auth.ready,
      (ready) => {
        if (ready) {
          stop()
          resolve()
        }
      },
      { immediate: true },
    )
  })
}
